import * as React from "react";

/**
 * Iron Rule #16 — below the `sm` breakpoint a wide table becomes a stack
 * of cards built from the same header and cells, so selection, links and
 * the row actions menu keep working.
 */

const BELOW_SM = "(max-width: 639px)";

export type DataTableResponsive = "auto" | "cards" | "table";

export function useBelowSm(): boolean {
  const [below, setBelow] = React.useState(belowSmNow);

  React.useEffect(() => {
    const mq = matchMediaOrNull();
    if (!mq) return;
    const onChange = () => setBelow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return below;
}

function matchMediaOrNull(): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  try {
    return window.matchMedia(BELOW_SM);
  } catch {
    return null;
  }
}

function belowSmNow(): boolean {
  return matchMediaOrNull()?.matches ?? false;
}

function displayNameOf(el: React.ReactElement): string | undefined {
  const type = el.type as { displayName?: string };
  return typeof type === "function" || typeof type === "object" ? type?.displayName : undefined;
}

function elements(node: React.ReactNode): React.ReactElement[] {
  const out: React.ReactElement[] = [];
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      out.push(...elements((child.props as { children?: React.ReactNode }).children));
      return;
    }
    out.push(child);
  });
  return out;
}

function findNamed(node: React.ReactNode, name: string): React.ReactElement | null {
  for (const el of elements(node)) {
    if (displayNameOf(el) === name) return el;
    const nested = findNamed((el.props as { children?: React.ReactNode }).children, name);
    if (nested) return nested;
  }
  return null;
}

function rowsOf(section: React.ReactElement | null): React.ReactElement[] {
  if (!section) return [];
  return elements((section.props as { children?: React.ReactNode }).children).filter(
    (el) => displayNameOf(el) === "TableRow",
  );
}

function cellsOf(row: React.ReactElement): React.ReactElement[] {
  return elements((row.props as { children?: React.ReactNode }).children).filter((el) => {
    const name = displayNameOf(el);
    return name === "TableHead" || name === "TableCell";
  });
}

function textOf(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) {
    return textOf((node.props as { children?: React.ReactNode }).children);
  }
  return "";
}

type ColumnRole = "select" | "actions" | "data";

function columnRole(label: string, index: number, total: number): ColumnRole {
  const clean = label.replace(/\s+/g, " ").trim();
  if (/^select(\b| row| all)/i.test(clean)) return "select";
  if (/^actions?$/i.test(clean)) return "actions";
  if (clean === "" && index === total - 1) return "actions";
  return "data";
}

/**
 * How many columns the table declares. `0` when the header isn't there yet
 * (callers then keep the table, including its skeleton).
 */
export function tableColumnCount(children: React.ReactNode): number {
  const header = findNamed(children, "TableHeader");
  const headRow = rowsOf(header)[0];
  return headRow ? cellsOf(headRow).length : 0;
}

export function DataTableCardList({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null {
  const header = findNamed(children, "TableHeader");
  const body = findNamed(children, "TableBody");
  const headRow = rowsOf(header)[0];
  const bodyRows = rowsOf(body);
  if (!headRow || bodyRows.length === 0) return null;

  const heads = cellsOf(headRow);
  const labels = heads.map((head) =>
    textOf((head.props as { children?: React.ReactNode }).children),
  );
  const roles = labels.map((label, index) => columnRole(label, index, labels.length));
  const primaryAt = roles.findIndex((role) => role === "data");
  const primaryIndex = primaryAt >= 0 ? primaryAt : 0;

  return (
    <ul data-slot="data-cards" className="divide-border/60 divide-y">
      {bodyRows.map((row, rowIndex) => {
        const cells = cellsOf(row);
        const content = (index: number) =>
          (cells[index]?.props as { children?: React.ReactNode } | undefined)?.children ?? null;
        const selectIndex = roles.indexOf("select");
        const actionsIndex = roles.lastIndexOf("actions");
        return (
          <li key={row.key ?? rowIndex} data-slot="data-card" className="px-4 py-3">
            <div className="flex items-start gap-3">
              {selectIndex >= 0 ? <div className="pt-0.5">{content(selectIndex)}</div> : null}
              <div className="min-w-0 flex-1 text-sm font-medium">{content(primaryIndex)}</div>
              {actionsIndex >= 0 ? <div className="shrink-0">{content(actionsIndex)}</div> : null}
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
              {roles.map((role, index) =>
                role === "data" && index !== primaryIndex ? (
                  <div key={labels[index] || index} className="min-w-0">
                    <dt className="text-muted-foreground text-[0.7rem]">{labels[index]}</dt>
                    <dd className="truncate text-sm">{content(index)}</dd>
                  </div>
                ) : null,
              )}
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
